import { useEffect, useMemo } from 'react';

import type { IUser } from 'store/declare';
import { useUsersStore } from 'store/users';

import { useAllUserList } from 'hooks/useAllUserList';

// хранилище очереди активных пользователей
// приоритеты пользователей https://www.notion.so/trendtech/4c73c7a8f63e4094be4873df2eb65df0
const activeUserQueue = new Set<string>();

/** Хук для работы с очередью активных пользователей */
export function useActiveUserQueue() {
    const { users } = useUsersStore();
    const { screenShareUser } = useAllUserList();

    const usersMethods = useMemo(() => {
        const firstUserId = (users[0] as IUser | undefined)?.id;

        return {
            /** Если очередь пустая - первый собеседник будет первый в очереди */
            manageEmptyQueue: () => {
                if (!activeUserQueue.size && firstUserId) activeUserQueue.add(firstUserId);
            },

            /** Нет собеседников */
            manageNoUsers: () => {
                if (!users.length) activeUserQueue.clear();
            },

            /** Есть только один собеседник */
            manageOneToOne: () => {
                if (users.length === 1 && firstUserId) activeUserQueue.add(firstUserId);
            },

            /** Проход по текущей очереди  с проверкой на предмет покинутых конференцию пользователей */
            manageUserLeave: () => {
                activeUserQueue.forEach(id => {
                    if (!users.find(user => user.id === id)) activeUserQueue.delete(id);
                });
            },

            /** Обновление очереди активных пользователей */
            updateQueueByTalkingProperty: () => {
                if (users.length > 1) {
                    users.forEach(user => {
                        const userId = user.id;

                        if (user.isTalking) activeUserQueue.add(userId);

                        if (activeUserQueue.size > 1) {
                            if (!user.isTalking) {
                                activeUserQueue.delete(userId);
                            }
                        }
                    });
                }
            },
        };
    }, [users]);

    // если есть пользователь демонстрирующий экран - очищаем очередь активных пользователей
    useEffect(() => {
        if (screenShareUser) activeUserQueue.clear();
    }, [screenShareUser]);

    return useMemo(
        () => ({
            getFirstInQueue: () => [...activeUserQueue][0],

            /** Обновление очереди активных пользователей */
            updateQueue: () => {
                if (!screenShareUser) {
                    usersMethods.manageNoUsers();
                    usersMethods.manageOneToOne();
                    usersMethods.manageUserLeave();
                    usersMethods.updateQueueByTalkingProperty();
                    usersMethods.manageEmptyQueue();
                }
            },
        }),
        [usersMethods, screenShareUser],
    );
}
